import React, { useMemo, useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { Nav, NavItem, NavLink, Spinner } from "reactstrap";
import classnames from "classnames";
import { connect } from "react-redux";

/**
 * Stacked columns, one segment per series, with Total and Monthly tabs.
 * Used for the per-channel value timelines on the PA dashboard.
 *
 * Expects the same graphs array LineGraph reads: [{ source, data: { labels, series } }].
 */
const StackedValueGraph = ({ useGraphQuery, queryParams = [], defaultActiveTab = 1, selectedCurrency }) => {
  const [activeTab, setActiveTab] = useState(defaultActiveTab);
  const { data, isLoading, isRefetching, isSuccess } = useGraphQuery(...queryParams);

  const graph = useMemo(() => data?.[activeTab], [data, activeTab]);

  const chartOptions = (graph) => {
    if (!graph?.data) {
      return {};
    }
    const currencyMultiplier = selectedCurrency?.value || 1;
    const series = graph.data.series.map((s) => ({
      name: s.name,
      color: s.color,
      type: "column",
      tooltip: { valueSuffix: ` ${selectedCurrency?.currency || ""}` },
      data: s.data.map((d) => {
        const value = typeof d === "number" && !isNaN(d) ? d : 0;
        return Math.round(value * currencyMultiplier);
      }),
    }));

    return {
      credits: { enabled: false },
      chart: { backgroundColor: "transparent" },
      title: { text: "" },
      xAxis: {
        categories: graph.data.labels,
        labels: { style: { color: "var(--text-primary)" } },
      },
      yAxis: {
        title: { text: graph.data.yTitle, style: { color: "var(--text-primary)" } },
        labels: { style: { color: "var(--text-primary)" } },
      },
      plotOptions: {
        column: { stacking: "normal", borderColor: null },
      },
      tooltip: { shared: true },
      series,
    };
  };

  return (
    <>
      {(isLoading || isRefetching) && (
        <Spinner color="primary" size="sm" style={{ position: "absolute", right: 20 }} />
      )}
      {isSuccess && (
        <>
          <div style={{ display: "flex", alignItems: "center" }} className="my-4 flex wrap flex-wrap">
            <div style={{ flex: 1 }}></div>
            <Nav pills className="navtab-bg center-pills">
              {Array.isArray(data) && data.map((g, index) => (
                <NavItem key={index}>
                  <NavLink
                    href="#"
                    className={classnames({ active: activeTab === index })}
                    onClick={(e) => { e.preventDefault(); setActiveTab(index); }}
                  >
                    {g.source}
                  </NavLink>
                </NavItem>
              ))}
            </Nav>
            <div style={{ flex: 1 }}></div>
          </div>
          {graph && <HighchartsReact highcharts={Highcharts} options={chartOptions(graph)} />}
        </>
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  selectedCurrency: state.currency.selectedCurrency,
});

export default connect(mapStateToProps)(StackedValueGraph);
